import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from '../ui/pagination';

// Build the page tokens to render: always the first + last page, the current page, and
// `siblingCount` neighbours each side; everything else collapses into an 'ellipsis' gap. Keeps the
// control a fixed width regardless of how many pages there are (e.g. 1 … 4 [5] 6 … 20).
export function paginationRange(
  page: number,
  pageCount: number,
  siblingCount = 1,
): (number | 'ellipsis')[] {
  // first + last + current + 2·siblings + 2 ellipsis slots. If the whole run fits, list it flat.
  const totalSlots = siblingCount * 2 + 5;
  if (pageCount <= totalSlots) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, pageCount);
  // Only collapse a side when it hides more than one page (a lone gap would be an ellipsis
  // standing in for a single number — show the number instead).
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < pageCount - 1;

  const tokens: (number | 'ellipsis')[] = [];
  if (showLeftEllipsis) {
    tokens.push(1, 'ellipsis');
  } else {
    for (let i = 1; i < leftSibling; i++) tokens.push(i);
  }
  for (let i = leftSibling; i <= rightSibling; i++) tokens.push(i);
  if (showRightEllipsis) {
    tokens.push('ellipsis', pageCount);
  } else {
    for (let i = rightSibling + 1; i <= pageCount; i++) tokens.push(i);
  }
  return tokens;
}

interface DataPaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
  className?: string;
}

// App-wide pager for client-side (already-fetched) lists — pair it with usePagination. Renders
// nothing for a single page, so callers can drop it in unconditionally.
export function DataPagination({
  page,
  pageCount,
  onPageChange,
  siblingCount = 1,
  className,
}: DataPaginationProps) {
  if (pageCount <= 1) return null;
  const tokens = paginationRange(page, pageCount, siblingCount);

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious onClick={() => onPageChange(page - 1)} disabled={page <= 1} />
        </PaginationItem>

        {tokens.map((token, i) =>
          token === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={token}>
              <PaginationLink
                isActive={token === page}
                aria-label={`Go to page ${token}`}
                onClick={() => onPageChange(token)}
              >
                {token}
              </PaginationLink>
            </PaginationItem>
          ),
        )}

        <PaginationItem>
          <PaginationNext onClick={() => onPageChange(page + 1)} disabled={page >= pageCount} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
